import React, { useEffect, useState, useRef } from "react";
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import Select2 from "react-select2-wrapper";
import "select2/dist/css/select2.min.css";
import userServices from "services/userServices";
import { useNotification } from "providers/NotificationProvider";
import { useTranslation } from "context/TranslationProvider";

const AssignModeratorModal = ({ ticket, onClose, onAssigned }) => {
  const [moderators, setModerators] = useState([]);
  const [assignedTo, setAssignedTo] = useState(ticket?.assignedTo?._id || "");
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const modalBodyRef = useRef(null);
  const { notify } = useNotification();
  const notifyRef = useRef(notify);

  useEffect(() => {
    const fetchModerators = async () => {
      try {
        const res = await userServices.fetchModerators();
        const dataObj = res?.data?.data || res?.data || res;
        const modsList = Array.isArray(dataObj) ? dataObj : (dataObj?.moderators || []);
        setModerators(modsList);
      } catch {
        notifyRef.current("error", "Failed to load moderators");
      }
    };
    fetchModerators();
  }, [notify]);

  const handleAssign = async () => {
    setLoading(true);
    try {
      const res = await userServices.updateSupportRequest(ticket._id, {
        assignedTo: assignedTo || null,
      });

      if (res?.success) {
        notify("success", t(res?.messageKey || "modal.assignTicket.assigned", res?.message));
        const assignedUser =
          moderators.find((m) => m._id === assignedTo) || null;
        onAssigned(assignedUser);
        onClose();
      } else {
        notify("error", t(res?.messageKey || res?.error || "modal.assignTicket.assignmentFailed", res?.message));
      }
    } catch {
      notify("error", t("modal.assignTicket.assignmentFailed", "Assignment failed"));
    } finally {
      setLoading(false);
    }
  };

  const options = [
    { id: "", text: t("page.support-tickets.ticketUnassigned", "Waiting for moderator") },
    ...moderators.map((m) => ({
      id: m._id,
      text: `${m.firstName} ${m.lastName} (${m.role?.name || "unknown"})`,
    })),
  ];

  const selectedValue = options.find((o) => o.id === assignedTo) || options[0];

  return (
    <>
      <ModalHeader className="border" toggle={onClose}>{t("modal.assignTicket.title", "Assign Moderator")}</ModalHeader>

      <ModalBody ref={modalBodyRef}>
        <div className="mb-3">
          <small className="text-muted">{t("page.support-tickets.ticket")}</small>
          <div className="font-weight-bold">{ticket.title}</div>
        </div>

        <Select2
          className="form-control"
          value={selectedValue.id}
          onChange={(e) => {
            setAssignedTo(e.target.value);
          }}
          options={{
            width: "100%",
            dropdownParent: modalBodyRef.current,
            data: options,
          }}
        />
      </ModalBody>

      <ModalFooter className="border">
        <Button color="secondary" onClick={onClose}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button color="warning" onClick={handleAssign} disabled={loading}>
          {loading && <Spinner size="sm" className="mr-2" />}
          {t("button.assign", "Assign")}
        </Button>
      </ModalFooter>
    </>
  );
};

export default AssignModeratorModal;
